import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["config"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useRef, useEffect } from 'react';
import { emit } from '../bridge';
import stringify from 'fast-json-stable-stringify';
import React from 'react';
import { useCallbackOne } from 'use-memo-one';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';

/*
 * The main purpose of this component is to emit a FORGE_CORE_MACRO_CONFIG_CHANGED event when the macro config changes,
 * so that the macro config app can be updated with the new config properly through useConfig hook.
 *
 * Importantly, in the legacy implementation, the children are remounted when the config change, as macro config is considered
 * as part of the product context. This was necessary to ensure that the macro config app was updated with the new config properly.
 * However, it is also an inefficient way to handle config changes, as it causes the entire app to remount and reload.
 *
 * There are few insights with above:
 * - the product context should never change in practice, as it contains mostly bootstrap information that should be static
 *   for the lifetime of the app
 * - there fore macro config should not be part of the product context, as it can change during the lifetime of the app
 *
 * In the new implementation (which targets to @forge/react version 11 or above), the app iframe are not remounted when the config changes.
 * Instead, the FORGE_CORE_MACRO_CONFIG_CHANGED event is emitted, and the macro config app is updated with the new config properly
 * through useConfig hook. This is a more correct and efficient way to handle config changes.
 */
export var ContextChangeObserverWrapper = function ContextChangeObserverWrapper(_ref) {
  var _context$extension;
  var context = _ref.context,
    children = _ref.children,
    getCurrentForgeReactMajorVersion = _ref.getCurrentForgeReactMajorVersion;
  var forgeReactMajorVersion = useRef(null);
  var initialConfigKey = useRef(null);
  var _ref2 = (_context$extension = context.extension) !== null && _context$extension !== void 0 ? _context$extension : {},
    config = _ref2.config,
    extensionWithoutConfig = _objectWithoutProperties(_ref2, _excluded);
  var currentConfigKey = stringify(config);
  var baseContextKey = stringify(_objectSpread(_objectSpread({}, context), {}, {
    extension: extensionWithoutConfig
  }));
  var trackWrappedComponentMounted = useWrappedComponentMountTracker();
  useEffect(function () {
    emit('FORGE_CORE_MACRO_CONFIG_CHANGED', {
      config: config
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfigKey]);
  if (forgeReactMajorVersion.current === null) {
    forgeReactMajorVersion.current = getCurrentForgeReactMajorVersion();
  }
  if (initialConfigKey.current === null) {
    initialConfigKey.current = currentConfigKey;
  }

  // for existing apps we need to ensure backwards compatibility
  var shouldContextKeyChangeOnConfigChange = forgeReactMajorVersion.current === null || forgeReactMajorVersion.current < 11;

  // if the app is using the new version of Forge react, we always uses the initial config key
  var key = baseContextKey + (shouldContextKeyChangeOnConfigChange ? currentConfigKey : initialConfigKey.current);
  return /*#__PURE__*/React.createElement(WrappedComponent, {
    key: key,
    onMounted: trackWrappedComponentMounted
  }, children);
};
var useWrappedComponentMountTracker = function useWrappedComponentMountTracker() {
  var _useForgeUiAnalyticsE = useForgeUiAnalyticsEvent(),
    trackExtensionRemounted = _useForgeUiAnalyticsE.trackExtensionRemounted;
  var mountedCount = useRef(0);
  var trackWrappedComponentMounted = useCallbackOne(function () {
    mountedCount.current += 1;
    if (mountedCount.current > 1) {
      trackExtensionRemounted({
        mountCount: mountedCount.current
      });
    }
  }, [mountedCount, trackExtensionRemounted]);
  return trackWrappedComponentMounted;
};
var WrappedComponent = function WrappedComponent(_ref3) {
  var children = _ref3.children,
    onMounted = _ref3.onMounted;
  useEffect(function () {
    onMounted();
  }, [onMounted]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
};